import {Button, DialogActions, DialogContent, Stack, InputLabel, MenuItem, FormControl, Select} from '@mui/material';
import DialogTitleWithClose from 'components/Generic/DialogTitle';
import GenericDialog from 'components/Generic/GenericDialog';
import GenericTextField from 'components/Generic/TextField';
import type {ModalDialogProps} from 'interfaces';
import {useState, type FC} from 'react';
import {useTranslation} from 'react-i18next';
import {type AddEditCompanyAlias, type Alias, AliasExternalSystemEnum} from 'client';

export const CompanyAliasModal: FC<ModalDialogProps<any, {parameters: Alias}>> = ({
  dialogProps,
  modalComponentProps: {onBeforeClose},
}) => {
  const [alias, setAlias] = useState<AddEditCompanyAlias>({
    externalSystem: AliasExternalSystemEnum.Fishtalk,
    alias: '',
  });
  const {t} = useTranslation('common');
  const [isValid, setIsValid] = useState<boolean>(false);

  const AliasExternalSystem: AliasExternalSystemEnum[] = Object.values(AliasExternalSystemEnum);

  const closeModal = (): void => {
    if (dialogProps.onClose) {
      dialogProps.onClose({}, 'escapeKeyDown');
    }
  };

  const sendModel = (): void => {
    validateModel(alias.alias);
    onBeforeClose && isValid && onBeforeClose({parameters: alias});
    dialogProps.onClose && isValid && dialogProps.onClose({}, 'escapeKeyDown');
  };

  const validateModel = (value: string): void => {
    value !== '' ? setIsValid(true) : setIsValid(false);
  };

  return (
    <GenericDialog {...dialogProps} data-testid="company-alias-modal">
      <DialogTitleWithClose
        onClose={() => {
          closeModal();
        }}
      >
        {t('addCompanyAlias')}
      </DialogTitleWithClose>
      <DialogContent sx={{padding: 0, paddingX: 3, minWidth: '400px'}}>
        <Stack gap={2} pt={2} width={'100%'}>
          <GenericTextField
            required={true}
            label={t('alias')}
            onChange={({target: {value}}) => {
              setAlias({...alias, alias: value});
              validateModel(value);
            }}
            data-testid="input-alias"
          />
          <SelectAliasExternalSystem
            alias={alias}
            options={AliasExternalSystem}
            setAliasExternalSystem={(externalSystem) => {
              setAlias({...alias, externalSystem});
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{paddingX: 3}}>
        <Button disabled={!isValid} variant="contained" onClick={sendModel} data-testid="add-company-alias">
          {t('add')}
        </Button>
      </DialogActions>
    </GenericDialog>
  );
};

const SelectAliasExternalSystem = ({
  alias,
  options,
  setAliasExternalSystem,
}: {
  alias: AddEditCompanyAlias;
  options: AliasExternalSystemEnum[];
  setAliasExternalSystem: (externalSystem: AliasExternalSystemEnum) => void;
}) => {
  const {t} = useTranslation('common');
  return (
    <FormControl fullWidth>
      <InputLabel id="select-alias-external-system-label">{t('externalSystem')}</InputLabel>
      <Select
        labelId="select-alias-external-system-label"
        data-testid="select-external-system"
        id="alias-external-system-select"
        label={t('externalSystem')}
        value={alias.externalSystem}
        onChange={({target: {value}}) => {
          setAliasExternalSystem(value as AliasExternalSystemEnum);
        }}
      >
        {options.map((key, index) => (
          <MenuItem value={key} key={index}>
            {key}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
